import { CircularProgress, IconButton } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import IdleTracker from "@saleor/components/IdleTracker";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { DropOffLocations_dropOffLocations_edges_node } from "@saleor/dropOff/types/DropOffLocations";
import useNotifier from "@saleor/hooks/useNotifier";
import { maybe } from "@saleor/misc";
import { OrderDetails } from "@saleor/motherOrders/types/OrderDetails";
import { OrderCourierEnum } from "@saleor/motherOrders/utils/enums";
import {
  failedReasonsNationwideDelivery,
  isExpressDelivery,
  isStandardDeliveryNewRTS
} from "@saleor/motherOrders/utils/handlers";
import { isValidHttpUrl } from "@saleor/motherOrders/utils/url";
import { FetchMoreProps } from "@saleor/types";
import {
  DeliveriesStatus,
  DeliveryTypeEnum,
  SmopOrderStatusEnum
} from "@saleor/types/globalTypes";
import React from "react";
import { useLocation } from "react-router-dom";

import { RiderDetails } from "../../types/RiderDetails";
import { searchRider_search_edges_node } from "../../types/searchRider";
import GrabCancelAutobookingModal from "../OrderList/components/GrabCancelAutobookingModal";
import InHouseRider from "./components/InHouseRider";
import LateDeliveryComponent from "./components/LateDelivery";
import OpsLateDeliveryComponent from "./components/OpsLateDelivery";
import ThirdPartyCancelButton from "./components/ThirdPartyCancelButton";
import ThirdPartyRider from "./components/ThirdPartyRider";
import ThirdPartyRiderTrackUrl from "./components/ThirdPartyTrackUrl";
import TransactionSection from "./components/TransactionSection";
import TwoGoRider from "./components/TwoGoRider";
import {
  dateTimeValidation,
  disableSubmitButton,
  handleBookingText,
  handleCancelGrabBooking,
  handleLateDeliveryReason,
  handleTimeTransaction,
  hasNoError
} from "./handlers";
import {
  get2GoManualRebookingData,
  getInhouseInitialForm,
  getThirdPartyInitialForm,
  getTransacInitialForm,
  initialThirdPartyRiderData,
  LateDeliveryList,
  OpsLateDeliveryList,
  transactionInitialErrorValidation
} from "./initialData";

export interface OrderAssignRiderDialogProps {
  riderList: searchRider_search_edges_node[];
  dropOffLocations: DropOffLocations_dropOffLocations_edges_node[];
  rider: RiderDetails | OrderDetails;
  onRefetch?: () => void;
  open: any;
  onClose: any;
  loading: boolean;
  thirdPartyBookingStatus: boolean;
  onSubmit: (data: {
    riderId?: string | null;
    isRider3pl: boolean;
    rider3plContactNo?: string | null;
    rider3plFirstName?: string | null;
    rider3plLastName?: string | null;
    courier?: string | null;
    timeDelivered?: any | null;
    timeDispatched?: any | null;
    lateReason?: string | null;
    lateDispatchReason?: string | null;
  }) => void;
  fetchRider: (query: string) => void;
  fetchMoreRider?: FetchMoreProps;
  onPandagoAutoCancel?: (cancelReason?: string) => void;
  onGrabExpressCancel?: () => void;
  on3plBooking?: any;
  onManualRebooking?: any;
  serverError?: {
    timeDispatched: string | null;
    timeDelivered: string | null;
  };
  setServerError?: (data: any) => void;
}

const useStyles = makeStyles(
  theme => ({
    avatar: {
      "&&:first-child": {
        paddingLeft: 0
      },
      width: 72
    },
    buttonActionFlex: {
      flexDirection: "column"
    },
    buttonActions: {
      display: "flex",
      flexDirection: "column",
      width: "100%"
    },
    buttonWidth: {
      margin: "3px",
      width: "100%"
    },
    checkboxCell: {
      paddingLeft: 0,
      width: 88
    },
    closeButton: {
      color: theme.palette.grey[500],
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1)
    },
    colName: {
      paddingLeft: 0,
      width: "100%"
    },
    content: {
      marginTop: "17px"
    },
    muiDialogContentOverrides: {
      "& .MuiInputLabel-root": {
        "& .MuiFormLabel-asterisk": {
          color: "#db3131"
        }
      }
    },
    riderType: {
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      marginBottom: "26px"
    },
    root: {
      "& label": {
        overflowX: "unset"
      }
    },
    sectionLabel: {
      margin: "0px",
      marginBottom: "6px",
      marginLeft: "5px"
    }
  }),
  { name: "OrderAssignRiderDialog" }
);

const OrderAssignRiderDialog: React.FC<OrderAssignRiderDialogProps> = props => {
  const {
    onClose,
    open,
    loading,
    riderList,
    rider,
    onSubmit,
    fetchRider,
    thirdPartyBookingStatus,
    fetchMoreRider,
    onGrabExpressCancel,
    onPandagoAutoCancel,
    on3plBooking,
    onManualRebooking,
    serverError,
    setServerError,
    onRefetch
  } = props;
  const notify = useNotifier();
  const classes = useStyles(props);
  const container = React.useRef<HTMLDivElement>();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const [selectedInHouseRider, setSelectedInHouseRider] = React.useState("");
  const [thirdPartyRider, setThirdPartyRider] = React.useState(
    initialThirdPartyRiderData
  );
  const [transactionTime, setTransactionTime] = React.useState({
    timeDelivered: "",
    timeDispatched: ""
  });
  const [isInHouse, setIsInHouse] = React.useState(true);
  const [errorValidation, setErrorValidation] = React.useState(
    transactionInitialErrorValidation
  );

  const isFromNotification = searchParams.get("isFromNotification") === "true";

  const deliveryCourierStatus =
    rider?.order?.thirdPartyRider?.deliveryCourier?.status;

  const isOnGoingBooking = handleCancelGrabBooking(deliveryCourierStatus);

  const [getInitialForm, setGetInitialForm] = React.useState(() =>
    getThirdPartyInitialForm(rider)
  );

  const [isCancelBooking, setIsCancelBooking] = React.useState(false);

  const is3PLCourierPandago =
    thirdPartyRider?.courier === OrderCourierEnum.PANDAGO_AUTO;

  const is3PLCourierGrabExpress =
    thirdPartyRider?.courier === OrderCourierEnum.GRAB_EXPRESS;

  const bookingButtonLabel = handleBookingText(
    deliveryCourierStatus,
    thirdPartyRider?.courier,
    isInHouse
  );
  const isOrderCompleted =
    rider?.order?.smopStatus === SmopOrderStatusEnum.COMPLETED;
  const isOrderCancelled =
    rider?.order?.smopStatus === SmopOrderStatusEnum.CANCELED;

  const [lateDeliveryReason, setLateDeliveryReason] = React.useState({
    otherReason: "",
    value: ""
  });
  const [opsLateDeliveryReason, setOpsLateDeliveryReason] = React.useState({
    otherReason: "",
    value: ""
  });

  const [weight, setWeight] = React.useState("");
  const [length, setLength] = React.useState("");
  const [width, setWidth] = React.useState("");
  const [height, setHeight] = React.useState("");

  const [
    displayDialogCancelAutobooking,
    setDisplayDialogCancelAutobooking
  ] = React.useState(false);
  const [
    displayDialogAssignRider,
    setDisplayDialogAssignRider
  ] = React.useState(false);

  const [hasTrackingUrl, setTrackingUrl] = React.useState("");

  const hasBooking = !!(
    rider?.order?.isRider3pl === false &&
    rider?.order.bookingTaskId &&
    rider?.order?.bookingTime
  );

  React.useEffect(() => {
    const getTrackingUrl = rider?.order?.deliveryCourier?.trackingUrl;
    setIsCancelBooking(false);
    setTrackingUrl(getTrackingUrl);
    setGetInitialForm(getThirdPartyInitialForm(rider));

    if (open) {
      setErrorValidation(transactionInitialErrorValidation);

      if (is3PLCourierPandago && getTrackingUrl) {
        setTrackingUrl(rider?.order?.deliveryCourier?.trackingUrl);
      }
    }
  }, [open, rider, is3PLCourierPandago]);

  React.useEffect(() => {
    const weight = parseFloat(rider?.order?.weight?.value);
    const calculatedValue = Math.pow(weight * 3500, 1 / 3);

    setWeight(Number(weight.toFixed(3)).toString());
    setLength(Number(calculatedValue.toFixed(3)).toString());
    setWidth(Number(calculatedValue.toFixed(3)).toString());
    setHeight(Number(calculatedValue.toFixed(3)).toString());
  }, [rider?.order?.weight?.value]);

  React.useEffect(() => {
    setLateDeliveryReason({
      otherReason: !LateDeliveryList.find(
        e => e.value === rider?.order?.lateDeliveryReason
      )
        ? rider?.order?.lateDeliveryReason
        : "",
      value: rider?.order?.lateDeliveryReason
    });
  }, [rider?.order?.lateDeliveryReason]);

  React.useEffect(() => {
    setOpsLateDeliveryReason({
      otherReason: !OpsLateDeliveryList.find(
        e => e.value === rider?.order?.lateDispatchReason
      )
        ? rider?.order?.lateDispatchReason
        : "",
      value: rider?.order?.lateDispatchReason
    });
  }, [rider?.order?.lateDispatchReason]);

  React.useEffect(() => {
    if (
      rider?.order?.shippingMethod?.deliveryType ===
      DeliveryTypeEnum.NATIONWIDE_DELIVERY
    ) {
      setIsInHouse(false);
    } else {
      setIsInHouse(!maybe(() => rider.order.isRider3pl));
    }
  }, [rider?.order?.isRider3pl]);

  const handleSubmit = () => {
    if (!isInHouse) {
      const trackingUrl = thirdPartyRider?.rider3plTrackingUrl?.trim() ?? "";
      if (trackingUrl && !isValidHttpUrl(trackingUrl)) {
        return;
      }
    }

    if (isInHouse) {
      handleInHouse();
    } else {
      handle3PL();
    }

    setThirdPartyRider(initialThirdPartyRiderData);
    setTransactionTime({ timeDelivered: "", timeDispatched: "" });
    setIsInHouse(!maybe(() => rider.order.isRider3pl));
    setDisplayDialogAssignRider(false);
  };

  const handleExpress = (handleSubmitSave: () => void) => {
    if (isExpressDelivery(rider.order.shippingMethod.deliveryType)) {
      const validate = dateTimeValidation(
        maybe(() => rider.order.created),
        transactionTime
      );
      if (hasNoError(validate)) {
        setErrorValidation(validate);
        handleSubmitSave();
        if (setServerError) {
          setServerError({
            ...serverError,
            timeDelivered: null,
            timeDispatched: null
          });
        }
        onClose();
      } else {
        setErrorValidation(validate);
      }
    } else {
      handleSubmitSave();
      if (setServerError) {
        setServerError({
          ...serverError,
          timeDelivered: null,
          timeDispatched: null
        });
      }
      onClose();
    }
  };

  const handleInHouse = () => {
    const handleSubmitInHouse = () =>
      onSubmit({
        courier: OrderCourierEnum.IN_HOUSE,
        isRider3pl: false,
        riderId:
          selectedInHouseRider !== ""
            ? selectedInHouseRider
            : maybe(() => rider.order.rider.id),
        ...handleTimeTransaction(transactionTime),
        lateDispatchReason:
          opsLateDeliveryReason.value === "OTHERS"
            ? opsLateDeliveryReason.otherReason
            : opsLateDeliveryReason.value,

        lateReason: handleLateDeliveryReason(
          lateDeliveryReason,
          rider?.order?.onTime
        )
      });

    handleExpress(handleSubmitInHouse);

    if (displayDialogCancelAutobooking) {
      setDisplayDialogCancelAutobooking(false);
    }
    if (displayDialogAssignRider) {
      setDisplayDialogAssignRider(false);
    }
  };

  const handle3PL = () => {
    const handleSubmitThirdParty = () => {
      if (is3PLCourierPandago || is3PLCourierGrabExpress) {
        on3plBooking({
          ...thirdPartyRider,
          contactPersonContactNo: `63${thirdPartyRider.contactPersonContactNo}`,
          isRider3pl: true,
          ...handleTimeTransaction(transactionTime),
          lateDispatchReason:
            opsLateDeliveryReason.value === "OTHERS"
              ? opsLateDeliveryReason.otherReason
              : opsLateDeliveryReason.value,
          lateReason: handleLateDeliveryReason(
            lateDeliveryReason,
            rider?.order?.onTime
          )
        });
      } else {
        onSubmit({
          ...thirdPartyRider,
          isRider3pl: true,
          ...handleTimeTransaction(transactionTime),
          lateDispatchReason:
            opsLateDeliveryReason.value === "OTHERS"
              ? opsLateDeliveryReason.otherReason
              : opsLateDeliveryReason.value,
          lateReason: handleLateDeliveryReason(
            lateDeliveryReason,
            rider?.order?.onTime
          )
        });
      }
    };

    handleExpress(handleSubmitThirdParty);
    if (displayDialogCancelAutobooking) {
      setDisplayDialogCancelAutobooking(false);
    }
    if (displayDialogAssignRider) {
      setDisplayDialogAssignRider(false);
    }
  };

  const book2goButton = failedReasonsNationwideDelivery(
    rider?.order?.deliveryCourier?.failedReason
  );

  const riderContent = () => {
    let contentToRender = null;

    if (book2goButton) {
      contentToRender = (
        <TwoGoRider
          initialForm={get2GoManualRebookingData(rider)}
          status={rider?.order?.smopStatus}
          failedReason={rider?.order?.deliveryCourier?.failedReason}
          isOnGoingBooking={isOnGoingBooking}
          rider={rider}
          weight={weight}
          height={height}
          length={length}
          width={width}
          setWeight={setWeight}
          setHeight={setHeight}
          setLength={setLength}
          setWidth={setWidth}
        />
      );
    } else if (isInHouse) {
      contentToRender = (
        <InHouseRider
          riders={riderList}
          rider={rider?.order}
          fetchRider={fetchRider}
          fetchMoreRider={fetchMoreRider}
          initialForm={getInhouseInitialForm(rider)}
          setSelectedRider={setSelectedInHouseRider}
          status={rider?.order?.smopStatus}
        />
      );
    } else {
      contentToRender = (
        <ThirdPartyRider
          pandaGoStatus={pandaGoStatus}
          initialForm={getThirdPartyInitialForm(rider)}
          setThirdPartyRider={setThirdPartyRider}
          status={rider?.order?.smopStatus}
          homeLocations={props.dropOffLocations}
          isOnGoingBooking={isOnGoingBooking}
          rider={rider}
        />
      );
    }

    return contentToRender;
  };

  const handleCancelReasonChange = ({ target: { value } }) => {
    setGetInitialForm(prevState => ({ ...prevState, cancelReason: value }));
  };

  const pandaGoStatus = {
    bookedStatus: [
      "PANDAGO_COURIER_ACCEPTED_DELIVERY",
      "PANDAGO_NEAR_VENDOR",
      "PANDAGO_PICKED_UP",
      "PANDAGO_NEAR_CUSTOMER",
      "PANDAGO_COURIER_LEFT_VENDOR",
      "PANDAGO__DELIVERED"
    ],
    bookingStatus: [
      "PANDAGO_NEW",
      "PANDAGO_RECEIVED",
      "PANDAGO_WAITING_FOR_TRANSPORT",
      "PANDAGO_ASSIGNED_TO_TRANSPORT"
    ],
    canceledStatus: ["PANDAGO_USER_CANCELLED", "PANDAGO_CANCELLED"]
  };

  const pandagoCancelReasonChoices = maybe(
    () => [
      {
        label: "Delivery ETA Too Long",
        value: "DELIVERY_ETA_TOO_LONG"
      },
      {
        label: "Order Mistake / Error",
        value: "MISTAKE_ERROR"
      },
      {
        label: "Vendor Initiated",
        value: "REASON_UNKNOWN"
      }
    ],
    []
  );

  const isCancelButtonVisible = [
    DeliveriesStatus.GENERAL_USER_CANCELLED,
    DeliveriesStatus.GRAB_USER_CANCELED,
    DeliveriesStatus.PANDAGO_USER_CANCELLED
  ].includes(deliveryCourierStatus);

  const isCancelButtonDisabled = [
    DeliveriesStatus.GENERAL_CANCELLED,
    DeliveriesStatus.GENERAL_IN_DELIVERY,
    DeliveriesStatus.GENERAL_COMPLETED,
    DeliveriesStatus.GRAB_CANCELED,
    DeliveriesStatus.GRAB_IN_DELIVERY,
    DeliveriesStatus.GRAB_COMPLETED
  ].includes(deliveryCourierStatus);

  const isValidCourier =
    (rider?.order?.courier === OrderCourierEnum.PANDAGO_AUTO &&
      is3PLCourierPandago) ||
    (rider?.order?.courier === OrderCourierEnum.GRAB_EXPRESS &&
      is3PLCourierGrabExpress);

  const shouldDisplayTrackingButton =
    !isInHouse &&
    !isCancelButtonVisible &&
    hasTrackingUrl &&
    isValidCourier &&
    !isOrderCompleted &&
    !isOrderCancelled;

  const assignRiderCardInactiveTimeout =
    rider?.order?.assignRiderCardInactiveTimeout;

  return (
    <Dialog
      onClose={() => {
        setIsInHouse(maybe(() => rider.order.isRider3pl) ? false : true);
        onClose();
      }}
      open={open}
      maxWidth="xs"
      fullWidth
      className={classes.root}
    >
      <>
        <DialogTitle id="txtAdminFulfillmentAssignRiderDialogTitle">
          Assign to Rider{" "}
          <IconButton
            id="icnAdminFulfillmentAssignRiderDialogClose"
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              onClose();
              setIsInHouse(maybe(() => rider.order.isRider3pl) ? false : true);
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          id="cmpAdminFulfillmentAssignRiderDialogContent"
          style={{ height: "55vh" }}
          ref={container}
          className={classes.muiDialogContentOverrides}
        >
          <>
            <GrabCancelAutobookingModal
              orderId={rider?.order?.id}
              displayDialog={displayDialogCancelAutobooking}
              onClose={() => setDisplayDialogCancelAutobooking(false)}
              onSaved={handleSubmit}
              title="Cancel Auto-booking"
              content="This order has been auto-booked.
              Are you sure you want to cancel the booking?"
              isForSavingCancelAutobooking={false}
            />
            <GrabCancelAutobookingModal
              orderId={rider?.order?.id}
              displayDialog={displayDialogAssignRider}
              onClose={() => setDisplayDialogAssignRider(false)}
              onSaved={handleSubmit}
              title="Assign to Rider"
              content="This order has been already assigned to a rider.
              Are you sure you want to re-assign to another rider?"
              isForSavingCancelAutobooking={false}
            />
            <h3
              id="txtAdminFulfillmentAssignRiderDialogContentRiderType"
              className={classes.sectionLabel}
            >
              Rider Type
            </h3>
            <div
              id="cmpAdminFulfillmentAssignRiderTabCourier"
              className={classes.riderType}
            >
              <Button
                id="btnAdminFulfillmentAssignRiderDialogInHouse"
                className={classes.buttonWidth}
                fullWidth
                variant={isInHouse ? "contained" : "outlined"}
                color={isInHouse ? "primary" : "default"}
                onClick={() => setIsInHouse(true)}
                disabled={
                  (pandaGoStatus.bookedStatus.includes(deliveryCourierStatus) &&
                    !!deliveryCourierStatus) ||
                  rider?.order?.smopStatus === SmopOrderStatusEnum.COMPLETED ||
                  rider?.order?.shippingMethod?.deliveryType ===
                    DeliveryTypeEnum.NATIONWIDE_DELIVERY
                }
              >
                In House
              </Button>
              <Button
                id="btnAdminFulfillmentAssignRiderDialog3pl"
                className={classes.buttonWidth}
                fullWidth
                variant={!isInHouse ? "contained" : "outlined"}
                color={!isInHouse ? "primary" : "default"}
                onClick={() => setIsInHouse(false)}
                disabled={
                  rider?.order?.smopStatus === SmopOrderStatusEnum.COMPLETED
                }
              >
                3PL
              </Button>
            </div>
            <div>
              {riderContent()}

              {(isExpressDelivery(rider?.order?.shippingMethod?.deliveryType) ||
                isStandardDeliveryNewRTS(
                  rider?.order?.shippingMethod?.deliveryType,
                  rider?.order?.smopStatus
                )) && (
                <TransactionSection
                  dateFieldsEditable={
                    rider?.order?.shippingMethod?.deliveryType ===
                    DeliveryTypeEnum.SAME_DAY
                  }
                  isDisabledPandago={pandaGoStatus.bookedStatus.includes(
                    deliveryCourierStatus
                  )}
                  initialForm={getTransacInitialForm(rider)}
                  setTransactionTime={setTransactionTime}
                  loading={loading}
                  status={rider?.order?.smopStatus}
                  error={errorValidation}
                  serverError={
                    serverError
                      ? serverError
                      : {
                          timeDelivered: null,
                          timeDispatched: null
                        }
                  }
                />
              )}
            </div>

            {loading ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center"
                }}
              >
                <CircularProgress style={{ alignSelf: "center" }} />
              </div>
            ) : (
              <>
                {(isExpressDelivery(
                  rider?.order?.shippingMethod?.deliveryType
                ) ||
                  isStandardDeliveryNewRTS(
                    rider?.order?.shippingMethod?.deliveryType,
                    rider?.order?.smopStatus
                  )) && (
                  <div>
                    <h3
                      style={{
                        margin: "0px",
                        marginBottom: "6px",
                        marginLeft: "5px"
                      }}
                    ></h3>
                    <div id="cmpAdminFulfillmentAssignRiderDialogOpsLateDelivery">
                      <OpsLateDeliveryComponent
                        lateDeliveryReason={opsLateDeliveryReason}
                        verticalType={rider?.order?.verticalType}
                        setLateDeliveryReason={setOpsLateDeliveryReason}
                      />
                    </div>
                  </div>
                )}

                {(rider?.order?.onTime !== null &&
                  !rider?.order?.onTime &&
                  isExpressDelivery(
                    rider?.order?.shippingMethod?.deliveryType
                  )) ||
                rider?.order?.shippingMethod?.deliveryType ===
                  DeliveryTypeEnum.SAME_DAY ? (
                  <div id="cmpAdminFulfillmentAssignRiderDialogLateDelivery">
                    <LateDeliveryComponent
                      lateDeliveryReason={lateDeliveryReason}
                      verticalType={rider?.order?.verticalType}
                      setLateDeliveryReason={setLateDeliveryReason}
                    />
                  </div>
                ) : null}
              </>
            )}
            {isCancelBooking && (
              <div className={classes.content}>
                <SingleSelectField
                  label="Booking Cancellation Reason"
                  onChange={handleCancelReasonChange}
                  choices={pandagoCancelReasonChoices}
                  name="cancelReason"
                  value={getInitialForm.cancelReason}
                  selectProps={{ required: true }}
                />
              </div>
            )}
          </>
        </DialogContent>
        <DialogActions className={classes.buttonActionFlex}>
          <div className={classes.buttonActions}>
            {shouldDisplayTrackingButton && (
              <ThirdPartyRiderTrackUrl
                thirdPartyCourier={rider?.order?.courier}
                onClick={() => window.open(hasTrackingUrl, "_blank", "popup")}
              />
            )}

            {book2goButton ? (
              <Button
                id="btnAdminFulfillmentAssignRiderDialogBook2Go"
                autoFocus
                onClick={() => {
                  onManualRebooking({
                    height,
                    length,
                    weight,
                    width
                  });
                }}
                variant="contained"
                color="primary"
                className={classes.buttonWidth}
              >
                BOOK 2GO
              </Button>
            ) : isOnGoingBooking ||
              isInHouse ||
              (!is3PLCourierGrabExpress && !is3PLCourierPandago) ? (
              <Button
                id="btnAdminFulfillmentAssignRiderDialogBook"
                disabled={disableSubmitButton(
                  isInHouse,
                  selectedInHouseRider,
                  thirdPartyRider,
                  loading,
                  getInhouseInitialForm(rider),
                  isOrderCompleted,
                  isOrderCancelled
                )}
                autoFocus
                onClick={() => {
                  if (
                    rider?.order?.isRider3pl === true &&
                    rider?.order?.bookingTime &&
                    rider?.order?.courier !== thirdPartyRider?.courier &&
                    !isInHouse
                  ) {
                    setDisplayDialogAssignRider(true);
                  } else if (
                    rider?.order?.isRider3pl === true &&
                    rider?.order?.bookingTime &&
                    isInHouse
                  ) {
                    setDisplayDialogAssignRider(true);
                  } else if (
                    rider?.order?.isRider3pl === false &&
                    rider?.order?.bookingTime &&
                    rider?.order?.bookingTaskId
                  ) {
                    setDisplayDialogCancelAutobooking(true);
                  } else {
                    handleSubmit();
                  }
                }}
                variant="contained"
                color="primary"
                className={classes.buttonWidth}
              >
                {bookingButtonLabel}
              </Button>
            ) : (
              <Button
                id="btnAdminFulfillmentAssignRiderDialogBookGrabExpress"
                disabled={isOrderCompleted || thirdPartyBookingStatus}
                onClick={() => {
                  if (
                    thirdPartyRider?.contactPersonContactNo?.length < 10 ||
                    !thirdPartyRider?.contactPersonContactNo.match(/^[0-9]+$/)
                  ) {
                    notify({
                      status: "error",
                      text: "Please enter a valid phone number"
                    });
                    return;
                  } else if (
                    thirdPartyRider.contactPersonFirstName?.length < 1
                  ) {
                    notify({
                      status: "error",
                      text: "Please enter a first name"
                    });
                    return;
                  } else if (
                    thirdPartyRider.contactPersonLastName?.length < 1
                  ) {
                    notify({
                      status: "error",
                      text: "Please enter a last name"
                    });
                    return;
                  }

                  const _3plBookPayload = {
                    contactPersonContactNo:
                      "63" + thirdPartyRider.contactPersonContactNo,
                    contactPersonFirstName:
                      thirdPartyRider.contactPersonFirstName,
                    contactPersonLastName:
                      thirdPartyRider.contactPersonLastName,
                    courier: thirdPartyRider.courier,
                    dropOffLocationId: thirdPartyRider.dropOffLocationId,
                    lateDispatchReason: thirdPartyRider.lateDispatchReason,
                    lateReason: thirdPartyRider.lateReason
                  };

                  on3plBooking({
                    ..._3plBookPayload,
                    lateDispatchReason:
                      opsLateDeliveryReason.value === "OTHERS"
                        ? opsLateDeliveryReason.otherReason
                        : opsLateDeliveryReason.value,
                    lateReason: handleLateDeliveryReason(
                      lateDeliveryReason,
                      rider?.order?.onTime
                    )
                  });
                }}
                variant="contained"
                color="primary"
                className={classes.buttonWidth}
              >
                {thirdPartyRider?.courier === OrderCourierEnum.GRAB_EXPRESS
                  ? "BOOK GRAB EXPRESS"
                  : is3PLCourierPandago && "ASSIGN RIDER"}
              </Button>
            )}

            {!isFromNotification && (
              <>
                {isOnGoingBooking &&
                  !pandaGoStatus.bookedStatus.includes(
                    deliveryCourierStatus
                  ) && (
                    <ThirdPartyCancelButton
                      orderId={rider?.order?.id}
                      refetch={onRefetch}
                      hasBooking={hasBooking}
                      cancelBookingData={getInitialForm.cancelReason}
                      isThirdPartyPandagoAuto={is3PLCourierPandago}
                      onPandagoCancel={onPandagoAutoCancel}
                      setIsCancelBooked={setIsCancelBooking}
                      onCancel={onGrabExpressCancel}
                      disabled={
                        rider?.order?.smopStatus ===
                          SmopOrderStatusEnum.SHIPPED ||
                        isCancelButtonDisabled ||
                        pandaGoStatus.bookedStatus.includes(
                          deliveryCourierStatus
                        )
                      }
                      onClose={onClose}
                    />
                  )}
              </>
            )}

            {assignRiderCardInactiveTimeout > 0 && (
              <IdleTracker
                seconds={assignRiderCardInactiveTimeout}
                onIdle={() => {
                  setIsInHouse(
                    maybe(() => rider.order.isRider3pl) ? false : true
                  );
                  onClose();
                  notify({
                    disableTimeout: true,
                    status: "warning",
                    text: "Your session has timed out due to inactivity."
                  });
                }}
              />
            )}
          </div>
        </DialogActions>
      </>
    </Dialog>
  );
};
OrderAssignRiderDialog.displayName = "OrderAssignRiderDialog";
export default OrderAssignRiderDialog;
